<template>
  <div>
    <v-card elevation="0" class="ma-sm-5 pa-5">
      <v-card-text>
        <v-row>
          <v-col class="px-3">
            <v-row no-gutters align="center">
              <h5 class="greyBold--text font-weight-bold mb-0">{{ $t('common:user') }}</h5>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn class="ml-2" icon @click="cardView = !cardView" v-bind="attrs" v-on="on">
                    <v-icon>list_alt</v-icon>
                  </v-btn>
                </template>
                <span v-if="cardView">{{ $t('buttons:table-view') }}</span>
                <span v-else>{{ $t('buttons:card-view') }}</span>
              </v-tooltip>
            </v-row>
          </v-col>
          <!-- TODO add search field for cardView -->
          <v-spacer></v-spacer>
          <v-col class="text-right">
            <BaseButton
                color="primary"
                :btn-text="$t('buttons:add-specific', { specific: $t('common:user') })"
                icon="add"
                @clickedButton="$refs.usersDialogForm.addUser(restUsers.options, restUsers.search)"
            />
          </v-col>
          <v-col cols="12" class="pt-0" v-show="!cardView">
            <DataTablePaginated
                ref="table"
                class="ma-1"
                :headers="isAdmin ? adminHeaders : headers"
                icon="edit"
                icon2="delete"
                icon3="send"
                dependsOnProperty3="registrationPending"
                :iconTooltip="$t('buttons:edit')"
                :iconTooltip2="$t('buttons:delete')"
                :iconTooltip3="$t('settings/profile:customer.user-management.tooltips.send-reg-mail')"
                @clickedButton1="editUser"
                @clickedButton2="deleteUser"
                @clickedButton3="resendRegMail"
                @updateTableData="updateTable"
                checkbox-on-property="main"
                checkbox-on-property2="active"
                sortBy="name"
                :filterName="isAdmin ? 'users' : null"
                @filterStatusChanged="filterStatusChange"
                @cacheCommit="$store.commit('settings/userProfile/storeUsersOverviewSearch', $event)"
                :cachedSearch="$store.state.settings.userProfile.overview.search"
            />
          </v-col>

          <v-col cols="12" class="pt-0" v-show="cardView">
            <SearchAndSortBar
                @updateData="updateTable"
                @cacheCommit="$store.commit('settings/userProfile/storeUsersOverviewSearch', $event)"
                :cachedSearch="$store.state.settings.userProfile.overview.search"
                :sorts="isAdmin ? adminHeaders : headers"
                default-sort-by="name"
                disable-filter
            />
            <v-row>
              <v-col cols="12" md="6" :lg="cardSize" v-for="(item, i) in restUsers.content" :key="i" >
                <ItemCard
                    :item="item"
                    :title="item.name"
                    :subtitle="item.email"
                    :description="'Username: ' + item.username"
                    hideActionBtn1
                    hideActionBtn2
                    hideActionBtn3
                    hideChart
                    hideViewBtn
                    cardElevation
                    @viewItem="editUserId(item.id)"
                    showDelete
                    @deleteItem="deleteUser(item)"
                />
              </v-col>
            </v-row>
            <v-pagination v-show="restUsers.content.length !== 0"
                          v-model="restUsers.options.page"
                          :length="totalPages"
                          :total-visible="7">
            </v-pagination>
          </v-col>
          <v-col v-if="restUsers.content.length === 0" class="text-center">
            <h6 class="greyTint--text mb-0">
              {{ $t('common:no-data-found') }}
            </h6>
          </v-col>
        </v-row>

        <TwoButtonDialog
            hideButton
            ref="deleteButton"
            :btn-text="$t('buttons:delete')"
            color="error"
            :dialog-title="$t('dialog:delete-btn-title', {name : $t('settings/profile:customer.user-management.dialog-part-1')})"
            :dialog-text="dialogText"
            :button1-text="$t('buttons:cancel')"
            :button2-text="$t('buttons:delete')"
            @clickedButton1="$refs.deleteButton.showDialog = false"
            @clickedButton2="deleteUserConfirmed()"
            :pureHtml="true"
        />

        <v-overlay :value="deleteLoader">
          <v-progress-circular
              indeterminate
              size="64"
          ></v-progress-circular>
        </v-overlay>

        <!-- Form Dialog for new User -->
        <UsersDialogForm ref="usersDialogForm" @updateUsers="updateTable"/>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import UsersService from '@/modules/core/services/users-service';

import Utils from '@/modules/core/services/utils';
import DataUtils from '@/modules/core/services/data-utils';

import Users from '@/modules/core/data/users';

import DataTablePaginated from '@/modules/core/components/DataTable/DataTablePaginated';
import BaseButton from '@/modules/core/components/Base/BaseButton';
import ItemCard from '@/modules/core/components/ItemCard'
import UsersDialogForm from './UsersDialogForm'
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import {Role} from '@/modules/core/data/roles';

import {mapState} from 'vuex'
import DateUtils from "@/modules/core/services/date-utils";
import SearchAndSortBar from "@/modules/core/components/SearchAndSortBar";

const MonthsToExpire = 6;

export default {
  name: 'CustomerUsers',
  components: {
    SearchAndSortBar,
    DataTablePaginated,
    BaseButton,
    ItemCard,
    UsersDialogForm,
    TwoButtonDialog,
  },
  data() {
    return {
      cardView: false,
      isFilterOpen: false,

      restUsers: {
        options: [],
        search: [],
        content: []
      },
      totalPages: null,

      userToDelete: null,
      deleteLoader: false,
      assignFacilityProcesses: null
    }
  },
  computed: {
    headers() {
      return [
        {text: "User-Name", value: 'username'},
        {text: "Main", value: 'checkbox', sortable: false},
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:email'), value: 'email'},
        {text: this.$t('common:active'), value: 'checkbox2', sortable: true}, // can be sortable, since checkbox2 is mapped to active in users-service.js
        {text: this.$t('settings/profile:customer.user-management.expires'), value: 'expiry', sortable: false},
        {text: "", value: "controls", sortable: false},
        {
          text: this.$t('common:customer'),
          value: 'customer',
          hide: true,
          filter: {
            type: 'multiselect',
            group: 0,
            order: 1,
            url: 'customer/all-customers'
          },
        }
      ]
    },
    adminHeaders() {
      return [
        {text: "User-Name", value: 'username'},
        {text: "Main", value: 'checkbox', sortable: false},
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:email'), value: 'email'},
        {text: this.$t('common:active'), value: 'checkbox2', sortable: true}, // can be sortable, since checkbox2 is mapped to active in users-service.js
        {text: this.$t('settings/profile:customer.user-management.first-login'), value: 'formattedFirstLogin'},
        {text: this.$t('settings/profile:customer.user-management.last-login'), value: 'formattedLastLogin'},
        {text: this.$t('settings/profile:customer.user-management.expires'), value: 'expiry', sortable: false},
        {text: "", value: "controls", sortable: false},
        {
          text: this.$t('common:customer'),
          value: 'customer',
          hide: true,
          filter: {
            type: 'multiselect',
            group: 0,
            order: 1,
            url: 'customer/all-customers'
          },
        }
      ]
    },
    cardSize() {
      if(this.profileSettingsDrawer) {
        return '6'
      } else {
        return '4'
      }
    },
    ...mapState('settings/layout', {
      profileSettingsDrawer: state => state.profileSettingsDrawer
    }),
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Admin);
      }
      return false;
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    dialogText() {
      let resultText = this.$t(
          'dialog:delete-btn-dialog',
          {name: this.$t('settings/profile:customer.user-management.dialog-part-2')}
      );

      if (this.assignFacilityProcesses !== null) {
        resultText = '';
        let facilities = '';
        let processes = '';
        let processesOriginList = this.assignFacilityProcesses.processes;
        let facilitiesOriginList = this.assignFacilityProcesses.facilities;

        for (let i = 0; i < facilitiesOriginList.length; i++) {
          facilities += '<li>' + facilitiesOriginList[i].name + '</li>';
        }

        for (let i = 0; i < processesOriginList.length; i++) {
          processes += '<li>' + processesOriginList[i].processName + '</li>';
        }

        if (facilities.length > 0) {
          resultText += this.$t('settings/profile:profile.delete-user-with-facility');
          resultText = resultText.replace('{{facility}}', facilities);
        }

        if (processes.length > 0) {
          resultText += this.$t('settings/profile:profile.delete-user-with-process');
          resultText = resultText.replace('{{process}}', processes);
        }

        resultText += this.$t('settings/profile:profile.delete-user-with-content-confirm');
      }

      return resultText;
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {

      // open docu dialog
      if ((to.name === 'CustomerUsers' && from.name === 'FacilityRoleInfoTab')
          || (to.name === 'CustomerUsers') && from.name === 'AddEditBuilding') {
        vm.$refs.usersDialogForm.addUser(vm.restUsers.options, vm.restUsers.search)
      }

      if(to.query.user) {
        vm.$refs.usersDialogForm.editUser(to.query.user);
      }
    });
  },
  methods: {
    fillTable(data) {
      this.totalPages = data.data.totalPages;

      if(this.$refs.table !== undefined) {
        data.data.content = DataUtils.parseDataToObjectArray(data.data.content, Users);
        this.$refs.table.fillTableData(data.data);
        this.restUsers.content = data.data.content
      }
    },
    updateTable(options, search, filter) {
      if (!search) search = '';
      this.$refs.table.loading = true;

      this.restUsers.options = options;
      this.restUsers.search = search;

      this.$store.commit('settings/userProfile/storeUsersOverviewOptions', options)
      if (!this.isFilterOpen || !filter || !filter.customer) {
        this.searchInTable(options, search)
      } else {
        this.filterTable(options, filter);
      }
    },

    formatLoginDates(users) {
      if (users && users.length > 0) {
        users.forEach((user) => {
          if (user.firstLogin) {
            user.formattedFirstLogin = DateUtils.parseDateTimeForDisplay(user.firstLogin);
          }

          if (user.lastLogin) {
            user.formattedLastLogin = DateUtils.parseDateTimeForDisplay(user.lastLogin);
          }
        })
      }
    },
    formatExpiryDate(users) {
      if (users) {
        users.forEach((user) => {

          if (user.active) {

            var expireDate;

            if (user.lastLogin) {
              expireDate = new Date(user.lastLogin);
            } else {

              const start = user.updatedAt;

              expireDate = DateUtils.parseStringToDate(start);
            }
            expireDate.setMonth(expireDate.getMonth() + MonthsToExpire);

            user.expiry = expireDate.toLocaleDateString();
          }
        })
      }
    },

    filterTable(options, filter) {
      UsersService.readFilteredPaginatedWithPrivilegesFromAPI(
          options.page - 1,
          options.itemsPerPage,
          options.sortBy,
          options.sortDesc,
          filter,
          (resp) => {
            this.formatLoginDates(resp.data.content);
            this.formatExpiryDate(resp.data.content);
            this.fillTable(resp);
          },
          (error) => {
            this.$refs.table.loading = false;
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:user')}))
            this.$logger.error(error);
          }
      );
    },
    searchInTable(options, search) {
      UsersService.readAllPaginatedWithPrivilegesFromAPI(
          options.page - 1,
          options.itemsPerPage,
          search,
          options.sortBy,
          options.sortDesc,
          (resp) => {
            this.formatLoginDates(resp.data.content);
            this.formatExpiryDate(resp.data.content);
            this.fillTable(resp);
          },
          (error) => {
            this.$refs.table.loading = false;
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:user')}))
            this.$logger.error(error);
          }
      );
    },
    editUser(item){
      this.editUserId(item.id);
    },
    editUserId(id) {
      this.$refs.usersDialogForm.editUser(id, this.restUsers.options, this.restUsers.search)
    },
    deleteUser(item) {
      this.deleteLoader = true;
      this.assignFacilityProcesses = null;

      UsersService.checkToDelete(
          item.id,
          (response) => {
            this.$refs.deleteButton.showDialog = true;
            this.deleteLoader = false;

            if (response.data.facilities.length > 0 || response.data.processes.length > 0) {
              this.assignFacilityProcesses = response.data;
            }
          },
          () => {
            this.deleteLoader = false;
          }
      )
      this.userToDelete = item;
    },
    deleteUserConfirmed() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);

      UsersService.deleteFromAPI(this.userToDelete,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:user')}));
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          this.updateTable(this.restUsers.options, this.restUsers.search)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:user')}))
        }
      )
    },
    resendRegMail(item) {
      let roleName = this.$t('enums:hierarchy.' + item.bookHierarchy);

      UsersService.resendRegisterMail(item.id, roleName,
          () => {
            Utils.showSuccessSnackbar(this.$t('settings/profile:customer.user-management.snackbar.send-reg-mail-success', {mail: item.email}))
          },
          (serviceName, error) => {
            if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
              Utils.showFailureSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
            } else {
              Utils.showFailureSnackbar(this.$t('settings/profile:customer.user-management.snackbar.send-reg-mail-failed', {mail: item.email}));
            }
          }
      )
    },
    updateFilter() {
      this.$refs.table.rerenderFilterPanel();
      this.callUpdate();
    },
    filterStatusChange(status) {
      this.isFilterOpen = status;
      this.$refs.table.rerenderFilterPanel();
      this.$refs.table.callTableUpdate();
    }
  }
}
</script>
